import type { Member } from '@hive/shared';
import { useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router';
import { useChannels, useChannelSettings, type Message, type NotifyLevel } from '@/data/comms';
import { queryKeys } from '@/data/keys';
import { useMembers } from '@/data/members';
import { desktopBridge } from '@/lib/bridge';
import { supabase } from '@/lib/supabase';
import { authorName } from './model';
import { claimNotifier, isNotifier, notificationText, releaseNotifier, shouldNotify } from './notify';

/**
 * Rings desktop notifications for new messages (Notification API, in the
 * browser and Electron alike), and keeps unread counts fresh. Mounted once in
 * the app shell. Asking for permission is left to a button in Comms.
 */
export function useCommsNotifications(me: Member): void {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const location = useLocation();
  const channels = useChannels();
  const settings = useChannelSettings();
  const members = useMembers();
  const latest = useRef({
    channels: channels.data,
    settings: settings.data,
    members: members.data,
    path: location.pathname,
    navigate,
  });
  useEffect(() => {
    latest.current = {
      channels: channels.data,
      settings: settings.data,
      members: members.data,
      path: location.pathname,
      navigate,
    };
  });

  useEffect(() => {
    if (document.hasFocus()) claimNotifier();
    window.addEventListener('focus', claimNotifier);
    window.addEventListener('beforeunload', releaseNotifier);
    return () => {
      window.removeEventListener('focus', claimNotifier);
      window.removeEventListener('beforeunload', releaseNotifier);
    };
  }, []);

  useEffect(() => {
    const client = supabase();
    let subscribedBefore = false;
    const subscription = client
      .channel('comms:notify')
      .on('postgres_changes', { event: '*', schema: 'public', table: 'messages' }, (payload) => {
        void queryClient.invalidateQueries({ queryKey: queryKeys.unread });
        if (payload.eventType !== 'INSERT') return;
        const message = payload.new as Message;
        if (typeof Notification === 'undefined' || Notification.permission !== 'granted') return;
        const { channels, settings, members, path, navigate } = latest.current;
        const channel = channels?.find((c) => c.id === message.channel_id);
        if (!channel) return;
        const viewing = /^\/comms\/([^/]+)/.exec(path)?.[1];
        const notify = (settings?.find((s) => s.channel_id === channel.id)?.notify as NotifyLevel | undefined) ?? 'all';
        const ring = shouldNotify(message, {
          meId: me.id,
          notify,
          viewingChannelId: viewing && (viewing === channel.name || viewing === channel.id) ? channel.id : null,
          focused: document.hasFocus(),
          notifier: isNotifier(),
        });
        if (!ring) return;
        const author = message.author_kind === 'claude' ? 'Claude' : authorName(message, members ?? []);
        const text = notificationText(message, channel.name, author);
        const note = new Notification(text.title, { body: text.body, tag: message.id });
        note.onclick = () => {
          window.focus();
          void desktopBridge()?.focusWindow();
          void navigate(`/comms/${channel.name}?message=${message.id}`);
          note.close();
        };
      })
      // Back after a dropped connection: refresh the unread counts that may have been missed.
      .subscribe((status) => {
        if (status !== 'SUBSCRIBED') return;
        if (subscribedBefore) void queryClient.invalidateQueries({ queryKey: queryKeys.unread });
        subscribedBefore = true;
      });
    return () => {
      void client.removeChannel(subscription);
    };
  }, [me.id, queryClient]);
}
